import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, AlertCircle, ShieldCheck } from 'lucide-react';
import { authApi } from '../api/client';
import {
    loginStart,
    loginSuccess,
    loginFailure,
    selectAuthStatus,
} from '../store/authSlice';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [localError, setLocalError] = useState(null);

    // Set once the password is accepted but a second factor is outstanding.
    // Holding it in state, not storage, means an abandoned half-login simply
    // disappears when the page is closed.
    const [mfaToken, setMfaToken] = useState(null);
    const [code, setCode] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const status = useSelector(selectAuthStatus);
    const isLoading = status === 'loading';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);
        dispatch(loginStart());
        try {
            const data = await authApi.login(email, password);
            // A correct password is not necessarily a session: when a second
            // factor is enrolled the server returns a challenge instead.
            if (data.mfaRequired) {
                setMfaToken(data.mfaToken);
                dispatch(loginFailure(null));
                return;
            }
            dispatch(loginSuccess(data));
            navigate('/');
        } catch (err) {
            // 401 stays generic — the server deliberately does not reveal
            // which half of the credential pair was wrong. A 429 is different
            // and must be shown as-is: the credentials may be perfectly
            // correct, and without this the user retries for the whole
            // lockout believing their password is broken.
            let msg;
            if (err.response?.status === 429) {
                msg = err.response.data?.message
                    || 'Too many sign-in attempts. Please wait a few minutes and try again.';
            } else if (err.response?.status === 401) {
                msg = 'Email or password is incorrect.';
            } else {
                msg = 'Could not sign in. Please try again.';
            }
            setLocalError(msg);
            dispatch(loginFailure(msg));
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setLocalError(null);
        dispatch(loginStart());
        try {
            const data = await authApi.verifyMfa(mfaToken, code);
            dispatch(loginSuccess(data));
            navigate('/');
        } catch (err) {
            // The server explains these precisely — an expired attempt, a
            // reused code, a lockout — and each needs a different action from
            // the user, so its message is shown rather than a generic one.
            const msg = err.response?.data?.message
                || 'That code was not accepted. Please try again.';
            setLocalError(msg);
            dispatch(loginFailure(msg));
            setCode('');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl font-semibold text-slate-900">
                        Loanworker
                    </h1>
                    <p className="text-slate-600 mt-1">
                        {mfaToken ? 'Enter your verification code' : 'Sign in to your account'}
                    </p>
                </div>

                {mfaToken ? (
                    <form
                        onSubmit={handleVerify}
                        className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4"
                    >
                        {localError && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                                <span>{localError}</span>
                            </div>
                        )}

                        <p className="text-sm text-slate-600">
                            Open your authenticator app and enter the six-digit code for
                            Loanworker. You can also use one of your recovery codes.
                        </p>

                        <div>
                            <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">
                                Verification code
                            </label>
                            <input
                                id="code"
                                type="text"
                                required
                                autoFocus
                                // one-time-code lets phones offer the code from
                                // the notification instead of app-switching.
                                autoComplete="one-time-code"
                                inputMode="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-md tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                                disabled={isLoading}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                        >
                            <ShieldCheck size={16} />
                            {isLoading ? 'Verifying...' : 'Verify'}
                        </button>

                        <button
                            type="button"
                            onClick={() => { setMfaToken(null); setCode(''); setLocalError(null); }}
                            className="w-full text-sm text-slate-500 hover:text-slate-800"
                        >
                            Start again
                        </button>
                    </form>
                ) : (
                <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4"
                >
                    {localError && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                            <span>{localError}</span>
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isLoading}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isLoading}
                        />
                        <p className="text-right mt-1">
                            <Link
                                to="/forgot-password"
                                className="text-xs text-brand-600 hover:text-brand-700"
                            >
                                Forgot password?
                            </Link>
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                        <LogIn size={16} />
                        {isLoading ? 'Signing in...' : 'Sign in'}
                    </button>
                </form>
                )}

                {!mfaToken && (
                    <p className="text-center text-sm text-slate-600 mt-6">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-brand-600 hover:text-brand-700 font-medium">
                            Register
                        </Link>
                    </p>
                )}
            </div>
        </div>
    );
}
